import React from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import IconWarningTriangle from "@components/CustomIcons/IconWarningTriangle";

export interface WarningDialogCstProps {
  open: boolean;
  title: string;
  description: React.ReactNode;
  onClose: () => void;
  closeLabel?: string;
  isLoading?: boolean;
  loadingLabel?: string;
}

const WarningDialogCst: React.FC<WarningDialogCstProps> = ({
  open,
  title,
  description,
  onClose,
  closeLabel = "close",
  isLoading = false,
  loadingLabel,
}) => {
  const theme = useTheme();

  const textSx = {
    fontSize: "14px",
    color: theme.palette.text.primary,
    lineHeight: 1.5,
    overflowWrap: "anywhere" as const,
    whiteSpace: "pre-line" as const,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "12px 12px 0",
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label={closeLabel}
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          padding: "0 24px 32px",
          textAlign: "center",
        }}
      >
        <IconWarningTriangle size={48} />

        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>

        {isLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CircularProgress size={20} />
            {loadingLabel && (
              <Typography sx={textSx}>{loadingLabel}</Typography>
            )}
          </Box>
        ) : typeof description === "string" ? (
          <Typography sx={textSx}>{description}</Typography>
        ) : (
          description
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WarningDialogCst;
